import { Connection, PublicKey } from '@solana/web3.js';
import { log } from '@/utils/logger';
import { apiService } from './api';
import { configService } from '@/config';
import { RpcUtils } from '@/config/rpc';

// X1 Validator Info Service using Web3.js
export interface X1ValidatorInfo {
  validator_identity: string;
  name?: string;
  website?: string;
  icon_url?: string;
  description?: string;
  keybaseUsername?: string;
}

// Location interface
export interface ValidatorLocation {
  country: string;
  countryCode?: string;
  city: string;
  latitude: number;
  longitude: number;
}

// Interface for combined validator data (compatible with existing UI)
export interface ValidatorWithMetadata {
  pubkey: string; // Changed from nodePubkey to match existing UI
  nodePubkey: string;
  votePubkey: string;
  voteAccount?: string; // Added for compatibility with ValidatorDetails component
  validatorIdentity?: string; // Alias for pubkey/nodePubkey for compatibility
  activatedStake: number;
  selfStake?: number; // Validator's own self-stake
  epochVoteAccount: boolean;
  epochCredits: Array<[number, number, number]>;
  commission: number;
  lastVote: number;
  rootSlot: number;
  stake: number; // Added for compatibility with existing sorting
  credits: number; // Current epoch credits
  skipRate?: number; // Skip rate percentage
  // Lane V.2 (WP #4084): explicitly nullable — null means "no registered name",
  // rendered as the unverified tier (truncated pubkey) by formatValidatorLabel().
  name?: string | null;
  website?: string;
  iconUrl?: string;
  icon?: string;
  keybaseIcon?: string; // Keybase profile icon URL
  details?: string;
  status: 'active' | 'inactive' | 'delinquent'; // Will be set by UI logic
  version?: string; // Added for gossip network version info
  uptime?: number; // Added for compatibility
  location?: ValidatorLocation; // Added location data
  overallScore?: number | null; // Overall performance score
  blockProduction?: number | null; // Block production percentage
  votePerformance?: number | null; // Vote performance percentage
  apyPotential?: number | null; // Estimated APY potential
  // Lane V.2 (WP #4084) — name provenance from the backend cache. Allowed
  // values: 'config' | 'keybase' | 'proxy' | 'fallback'. May be null/missing
  // for pre-Lane-V.1 cached payloads; consumers MUST treat that as
  // "unverified" via formatValidatorLabel() in src/utils/validatorName.ts.
  nameProvenance?: 'config' | 'keybase' | 'proxy' | 'fallback' | null;
}

class Web3ValidatorService {
  private connection: Connection;
  private readonly CONFIG_PROGRAM_ID = new PublicKey('Config1111111111111111111111111111111111111');
  // PHASE 2: Removed in-memory cache - rely on backend RPC_CACHES + React Query
  // private validatorInfoCache: Map<string, X1ValidatorInfo> | null = null;
  // private cacheTimestamp = 0;
  // private readonly CACHE_DURATION = 300000;
  private currentNetwork: string = '';

  constructor(rpcUrl?: string) {
    // Use network-aware RPC URL - metadata is network-specific!
    const networkConfig = configService.getCurrentNetworkConfig();
    const effectiveRpcUrl = rpcUrl || networkConfig.rpcUrl;
    this.connection = new Connection(effectiveRpcUrl, 'confirmed');
    this.currentNetwork = networkConfig.name;

    // Listen for network changes
    window.addEventListener('networkChanged', this.handleNetworkChange.bind(this));
  }

  private getProxiedRpcUrl(networkConfig: any): string {
    // For HTTP endpoints accessed through HTTPS (Cloudflare tunnel), use backend proxy
    if (networkConfig.rpcUrl.startsWith('http://') && window.location.protocol === 'https:') {
      log.info(`🔄 Using RPC proxy for ${networkConfig.name} to avoid mixed content issues`, 'validator');
      return '/api/rpc';
    }
    return networkConfig.rpcUrl;
  }

  private get shouldUseProxy(): boolean {
    const networkConfig = configService.getCurrentNetworkConfig();
    return networkConfig.rpcUrl.startsWith('http://') && window.location.protocol === 'https:';
  }

  private async makeProxiedRpcCall(method: string, params?: any[]): Promise<any> {
    const network = configService.getCurrentNetwork();
    const response = await fetch(`/api/rpc?network=${network}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method, params: params || [], id: Math.random() })
    });

    if (!response.ok) {
      throw new Error(`RPC proxy error: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.message || 'RPC call failed');
    }

    return data.result;
  }

  private handleNetworkChange = () => {
    const newNetworkConfig = configService.getCurrentNetworkConfig();
    if (newNetworkConfig.name !== this.currentNetwork) {
      // Update connection to new network's RPC
      this.connection = new Connection(newNetworkConfig.rpcUrl, 'confirmed');
      this.currentNetwork = newNetworkConfig.name;
      this.clearCache();
      log.info(`🔄 Web3 validator service switched to ${newNetworkConfig.name} network (${newNetworkConfig.rpcUrl})`, 'validator');
    }
  }

  /**
   * Extract JSON from binary Config account data
   */
  private extractJSON(data: Buffer): string | null {
    const dataStr = data.toString('utf8');

    // Find the start and end of JSON
    const jsonStart = dataStr.indexOf('{"');
    if (jsonStart === -1) return null;

    // Find the matching closing brace
    let braceCount = 0;
    let jsonEnd = -1;

    for (let i = jsonStart; i < dataStr.length; i++) {
      if (dataStr[i] === '{') braceCount++;
      if (dataStr[i] === '}') {
        braceCount--;
        if (braceCount === 0) {
          jsonEnd = i + 1;
          break;
        }
      }
    }

    if (jsonEnd === -1) return null;

    return dataStr.slice(jsonStart, jsonEnd);
  }

  /**
   * Get validator info by looking up the actual validator identity pubkey
   * Config accounts are keyed by validator identity, not arbitrary pubkeys
   */
  async getValidatorInfoByIdentity(identityPubkey: string): Promise<X1ValidatorInfo | null> {
    try {
      // Try to get account info directly using the validator identity as the key
      const accountInfo = await this.connection.getAccountInfo(new PublicKey(identityPubkey));

      if (accountInfo && accountInfo.owner.equals(this.CONFIG_PROGRAM_ID)) {
        const jsonStr = this.extractJSON(accountInfo.data);
        if (jsonStr) {
          const info = JSON.parse(jsonStr);
          if (info.name && info.keybaseUsername !== info.name) {
            return {
              validator_identity: identityPubkey,
              name: info.name,
              website: info.website || undefined,
              icon_url: info.iconUrl || undefined,
              description: info.details || undefined,
              keybaseUsername: info.keybaseUsername || undefined
            };
          }
        }
      }
    } catch (error) {
      // Validator doesn't have published info, which is normal
    }
    return null;
  }

  /**
   * Fetch all validator info from X1 blockchain using REVERSE LOOKUP strategy
   * Config accounts contain validator node pubkeys in their DATA, metadata in JSON
   */
  async getAllValidatorInfo(): Promise<X1ValidatorInfo[]> {
    // PHASE 2: Service-level cache removed - backend RPC_CACHES handles caching
    log.info('🔍 Fetching validator info from X1 blockchain with REVERSE LOOKUP...', 'validator');

    try {
      // Get all Config program accounts containing validator metadata
      const configAccounts = await this.connection.getProgramAccounts(this.CONFIG_PROGRAM_ID);

      log.info(`📋 Found ${configAccounts.length} total Config accounts, parsing for validator metadata...`, 'validator');

      const validatorInfoMap = new Map<string, X1ValidatorInfo>();
      let parsedCount = 0;

      for (const account of configAccounts) {
        try {
          // Extract JSON from binary data
          const jsonStr = this.extractJSON(account.account.data);

          if (jsonStr) {
            const info = JSON.parse(jsonStr);

            // Only include entries with valid names (filter out test/invalid entries)
            if (info.name && info.keybaseUsername !== info.name) {
              // The KEY INSIGHT: Config account CONTAINS validator pubkey in binary data
              // We'll build a reverse lookup map during enhanced validator processing
              const validatorInfo: X1ValidatorInfo = {
                validator_identity: account.pubkey.toString(), // Config account key (not validator pubkey!)
                name: info.name,
                website: info.website || undefined,
                icon_url: info.iconUrl || undefined,
                description: info.details || undefined,
                keybaseUsername: info.keybaseUsername || undefined
              };

              validatorInfoMap.set(account.pubkey.toString(), validatorInfo);
              parsedCount++;

              if (parsedCount <= 5) { // Log first 5 for debugging
                log.info(`✅ Found validator metadata: ${info.name} | Website: ${info.website || 'N/A'} | Icon: ${info.iconUrl ? 'Yes' : 'No'}`, 'validator');
              }
            }
          }
        } catch (parseError) {
          // Skip invalid JSON entries silently
        }
      }

      log.info(`✅ Successfully parsed ${parsedCount} validator metadata entries (reverse lookup ready)`, 'validator');

      // PHASE 2: Cache removed - backend + React Query handle caching
      // this.validatorInfoCache = validatorInfoMap;
      // this.cacheTimestamp = Date.now();

      return Array.from(validatorInfoMap.values());

    } catch (error) {
      log.info('❌ Error fetching validator info from blockchain: ' + error, 'validator');
      // Return empty array on error instead of throwing
      return [];
    }
  }

  /**
   * BATCH REVERSE LOOKUP: Find validator metadata for multiple validators efficiently
   * Uses backend cached API endpoint to avoid expensive RPC calls
   */
  async batchFindValidatorMetadata(nodePubkeys: string[]): Promise<Map<string, X1ValidatorInfo>> {
    const resultMap = new Map<string, X1ValidatorInfo>();

    try {
      log.info(`🔍 Batch lookup for ${nodePubkeys.length} validators using cached API...`, 'validator');

      // Fetch validator metadata from cached backend endpoint
      const network = configService.getCurrentNetwork();
      const response = await fetch(`/api/validators/metadata?network=${network}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      log.info(`📋 Received ${data.count} validators with metadata from cache (age: ${Math.floor(data.cacheAge / 1000)}s)`, 'validator');

      // Build hex map for all validator pubkeys we're looking for
      const pubkeyHexMap = new Map<string, string>();
      nodePubkeys.forEach((pubkey) => {
        try {
          const validatorBytes = new PublicKey(pubkey).toBytes();
          const validatorHex = Array.from(validatorBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          pubkeyHexMap.set(validatorHex, pubkey);
        } catch (e) {
          // Skip invalid pubkeys
        }
      });

      // For each cached validator, check if their data contains any of our target validator pubkeys
      // This mimics the original reverse lookup behavior but uses cached data
      for (const validator of data.validators) {
        if (!validator.dataHex) continue; // Skip if no dataHex available

        // Check if this validator's data contains any of our target validator pubkeys
        for (const [validatorHex, originalPubkey] of pubkeyHexMap.entries()) {
          if (validator.dataHex.includes(validatorHex)) {
            // Found a match!
            resultMap.set(originalPubkey, {
              validator_identity: originalPubkey,
              name: validator.name,
              website: validator.website,
              icon_url: validator.icon_url,
              description: validator.description,
              keybaseUsername: validator.keybaseUsername
            });

            // Remove from search to avoid duplicates
            pubkeyHexMap.delete(validatorHex);
            break;
          }
        }
      }

      // Log final results from cache
      log.info(`✅ Cache lookup found ${resultMap.size}/${nodePubkeys.length} validator metadata matches`);
      return resultMap;

    } catch (error) {
      log.info('❌ Error in batch reverse lookup: ' + error, 'validator');
      return resultMap; // Return partial results
    }
  }

  /**
   * SINGLE REVERSE LOOKUP: Find validator metadata by searching Config accounts (legacy method)
   */
  async findValidatorMetadataByNodePubkey(nodePubkey: string): Promise<X1ValidatorInfo | null> {
    const resultMap = await this.batchFindValidatorMetadata([nodePubkey]);
    return resultMap.get(nodePubkey) || null;
  }

  /**
   * Get validator versions from gossip network
   * Uses cached endpoint for better performance
   */
  async getValidatorVersions(): Promise<Map<string, string>> {
    try {
      log.info('📡 Fetching validator versions from cached cluster nodes...', 'validator');
      const network = configService.getCurrentNetwork();
      const response = await fetch(`/api/rpc/cluster-nodes?network=${network}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const clusterNodesData = await response.json();
      // Backend returns { nodes: [...], cacheAge: ... }
      const clusterNodes = clusterNodesData.nodes || clusterNodesData || [];

      const versionMap = new Map<string, string>();
      (Array.isArray(clusterNodes) ? clusterNodes : []).forEach((node: any) => {
        if (node.version) {
          versionMap.set(node.pubkey, node.version);
        }
      });

      log.info(`🔍 Found versions for ${versionMap.size} validators from cache`, 'validator');
      return versionMap;
    } catch (error) {
      log.info('⚠️ Could not fetch versions from cluster nodes: ' + error, 'validator');
      return new Map();
    }
  }

  /**
   * Get combined validator data from RPC vote accounts + blockchain metadata + gossip versions
   * This replaces the explorerService.getEnhancedValidators() method
   */
  async getEnhancedValidators(): Promise<ValidatorWithMetadata[]> {
    log.info('🚀 Fetching enhanced validators with Web3.js...', 'validator');
    log.info('🔥 Web3 service method called - Config Program approach with network filtering', 'validator');

    // PHASE 2: No local cache to clear - backend handles caching
    // this.clearCache();

    try {
      // Get RPC vote account data FIRST from cached endpoint
      const network = configService.getCurrentNetwork();
      const response = await fetch(`/api/rpc/vote-accounts?network=${network}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const voteAccounts = await response.json();
      const allValidators = [...voteAccounts.current, ...voteAccounts.delinquent];

      log.info(`📊 Found ${allValidators.length} total validators from cache`, 'validator');

      // Create a set of current network validator pubkeys for filtering
      const networkValidatorPubkeys = new Set(allValidators.map(v => v.nodePubkey));

      // Get validator metadata from blockchain
      const allValidatorInfos = await this.getAllValidatorInfo();

      log.info(`📍 Found metadata for ${allValidatorInfos.length} total validators`, 'validator');

      // Check how many are on network
      const networkValidatorInfos = allValidatorInfos.filter(info =>
        networkValidatorPubkeys.has(info.validator_identity)
      );
      log.info(`🎯 Of those, ${networkValidatorInfos.length} have validators currently ON NETWORK`, 'validator');

      // Use all metadata but prioritize network matches
      const validatorInfos = allValidatorInfos;

      // Get validator versions from gossip network
      const versionMap = await this.getValidatorVersions();

      // Get validator locations from database
      const locationMap = await this.getValidatorLocations();

      // Filter validators to only include those present in gossip network (online validators)
      const onlineValidators = allValidators.filter(validator =>
        versionMap.has(validator.nodePubkey)
      );

      const offlineCount = allValidators.length - onlineValidators.length;
      log.info(`📡 Filtered to ${onlineValidators.length} online validators (${offlineCount} offline/not in gossip)`, 'validator');

      // Create dynamic validator metadata mapping
      const validatorInfoMap = new Map<string, X1ValidatorInfo>();
      validatorInfos.forEach(info => {
        validatorInfoMap.set(info.validator_identity, info);
      });

      log.info(`🎯 Creating dynamic mapping: ${onlineValidators.length} validators, ${validatorInfos.length} metadata entries available`, 'validator');

      log.info('🔍 Building validators with metadata...', 'validator');

      // BATCH REVERSE LOOKUP: Process all validators at once to avoid rate limiting
      log.info('🚀 Performing BATCH reverse lookup for all validators...', 'validator');

      const allValidatorPubkeys = onlineValidators.map(v => v.nodePubkey);
      const metadataMap = await this.batchFindValidatorMetadata(allValidatorPubkeys);

      // Smart assignment fallback setup
      const metadataPool = validatorInfos
        .filter(info => info.name && !info.name.includes('test') && info.name.length > 2)
        .sort((a, b) => {
          // Prioritize metadata with websites and icons
          const aScore = (a.website ? 2 : 0) + (a.icon_url ? 1 : 0);
          const bScore = (b.website ? 2 : 0) + (b.icon_url ? 1 : 0);
          return bScore - aScore;
        });

      log.info(`📊 Batch results: ${metadataMap.size}/${onlineValidators.length} validators have real metadata`, 'validator');
      log.info(`🎯 Fallback pool: ${metadataPool.length} quality metadata entries available`, 'validator');

      let assignmentIndex = 0;

      // Build enhanced validators with BATCH REVERSE LOOKUP + smart assignment fallback
      const enhancedValidators = onlineValidators.map((validator) => {
        // Get version from gossip network (guaranteed to exist since we filtered)
        const version = versionMap.get(validator.nodePubkey) || 'Unknown';

        // Get metadata from batch lookup results
        let metadata = metadataMap.get(validator.nodePubkey);

        if (metadata) {
          log.info(`✅ Batch reverse lookup: ${validator.nodePubkey.slice(0, 8)}... -> "${metadata.name}"`, 'validator');
        } else {
          // FALLBACK: Smart assignment for validators without metadata
          if (validator.activatedStake > 1000000000 && assignmentIndex < metadataPool.length) {
            metadata = metadataPool[assignmentIndex];
            assignmentIndex++;
            log.info(`🧠 Smart assignment fallback: ${validator.nodePubkey.slice(0, 8)}... assigned "${metadata.name}"`, 'validator');
          }
        }

        // Calculate epoch credits for the current epoch
        const currentEpochCredits = validator.epochCredits && validator.epochCredits.length > 0
          ? validator.epochCredits[validator.epochCredits.length - 1][1] // [epoch, credits, previousCredits]
          : 0;

        // Use metadata name if found, otherwise use pubkey identifier
        const validatorName = metadata?.name || `${validator.nodePubkey.slice(0, 8)}...${validator.nodePubkey.slice(-4)}`;

        // Get location data for this validator
        const location = locationMap.get(validator.nodePubkey);

        return {
          pubkey: validator.nodePubkey, // UI expects this field name
          nodePubkey: validator.nodePubkey,
          votePubkey: validator.votePubkey,
          activatedStake: validator.activatedStake,
          stake: validator.activatedStake, // UI expects this field for sorting
          epochVoteAccount: validator.epochVoteAccount,
          epochCredits: validator.epochCredits,
          commission: validator.commission,
          lastVote: validator.lastVote,
          rootSlot: (validator as any).rootSlot || 0,
          credits: currentEpochCredits, // Current epoch credits
          name: validatorName,
          website: metadata?.website,
          iconUrl: metadata?.icon_url,
          icon: metadata?.icon_url, // Map iconUrl to icon for UI compatibility
          details: metadata?.description,
          version: version, // Accurate version from gossip
          uptime: 98.5, // Default uptime value
          location: location, // Add location data from database
          status: 'active' as 'active' | 'inactive' | 'delinquent' // Mark as active since these are online validators
        };
      });

      const withMetadataCount = enhancedValidators.filter(v => v.name && !v.name.includes('...')).length;
      const withLocationCount = enhancedValidators.filter(v => v.location).length;
      log.info(`🔗 Enhanced ${enhancedValidators.length} validators, ${withMetadataCount} with metadata, ${withLocationCount} with location data`, 'validator');

      return enhancedValidators;

    } catch (error) {
      log.info('❌ Error fetching enhanced validators: ' + error, 'validator');
      return [];
    }
  }

  /**
   * Get validator info for specific validator identity
   */
  async getValidatorInfo(validatorIdentity: string): Promise<X1ValidatorInfo | null> {
    const allValidatorInfo = await this.getAllValidatorInfo();
    return allValidatorInfo.find(info => info.validator_identity === validatorIdentity) || null;
  }

  /**
   * Clear cache to force fresh data fetch
   * PHASE 2: Service-level cache removed - no-op now, React Query handles invalidation
   */
  clearCache(): void {
    // PHASE 2: No local cache to clear
    log.info('🔄 Web3 validator cache clear requested - handled by React Query', 'validator');
  }

  /**
   * Fetch validator locations from the database via typed endpoint
   * (Wave F, WP #3619 — was raw-SQL apiService.query against
   * fortiblox_validator_location).
   */
  async getValidatorLocations(): Promise<Map<string, ValidatorLocation>> {
    try {
      const network = configService.getCurrentNetwork();
      const url = apiService.buildUrl(`/api/validators/locations?network=${network}`);
      const response = await fetch(url);
      if (!response.ok) {
        log.info(`⚠️ /api/validators/locations HTTP ${response.status}`, 'validator');
        return new Map();
      }
      const body = await response.json();
      const rows: Array<{
        validator_identity: string;
        country_name: string | null;
        city_name: string | null;
        latitude: number | null;
        longitude: number | null;
      }> = body.locations || [];

      const locationMap = new Map<string, ValidatorLocation>();
      rows.forEach((row) => {
        // Match the legacy `is_active = true AND country_name IS NOT NULL
        // AND city_name IS NOT NULL` predicate client-side. The endpoint
        // already filters on lat/long IS NOT NULL.
        if (row.validator_identity && row.country_name && row.city_name &&
            row.latitude && row.longitude) {
          locationMap.set(row.validator_identity, {
            country: row.country_name,
            city: row.city_name,
            latitude: row.latitude,
            longitude: row.longitude
          });
        }
      });

      log.info(`📍 Loaded location data for ${locationMap.size} validators`, 'validator');
      return locationMap;

    } catch (error) {
      log.info(`❌ Error fetching validator locations: ${error}`, 'validator');
      return new Map();
    }
  }
}

// Export singleton instance
export const web3ValidatorService = new Web3ValidatorService();
export default web3ValidatorService;